import { returnToCreationTab } from './creation-tab-context';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useTranslation } from 'react-i18next';
import type { NavigationScreen } from 'app/types/navigation';
import { ListingType, ListingSubtype } from 'app/types/listing';
import type { ButtonProps } from 'app/design/primitives/button';
import { Button } from 'app/design/primitives/button';
import { SERVICE_CATEGORY_ID, useAllServiceCategories } from 'app/network/category';
import type { ServiceCategory } from 'app/types/category';
import React, { useEffect } from 'react';
import { ChatBubble } from 'app/design/icons/chat-bubble';
import { useMyProfile } from 'app/network/profile';
import { Platform, Pressable } from 'react-native';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { useSetAtom } from 'jotai/index';
import { selectedServiceCategoryAtom } from 'app/atoms';
import { usePageTitle } from 'app/utils/web-helpers';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronLeft, IconHelpCircle, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { navigateToListingWithParams } from 'app/screens/billboard/navigator-helper';
const theme = createDSTheme();

const ServiceCategoriesScreenComponent: NavigationScreen<'serviceCategories'> = ({ navigation, route }) => {
  const { t } = useTranslation('new');

  const setPageTitle = usePageTitle('serviceCategories');
  useEffect(() => setPageTitle('prompt.serviceRequest.title'), [setPageTitle]);

  const { data: myProfile } = useMyProfile();
  const { data: serviceCategories } = useAllServiceCategories();

  const setSelectedCategory = useSetAtom(selectedServiceCategoryAtom);

  const openNewWithGuard = useOpenNewWithGuard();

  const navigateToNew = (serviceCategoryId: ServiceCategory['id']) => {
    if (route.params?.goBackTo) {
      // navigation.popTo()
      if (route.params.goBackTo === 'publish') {
        returnToCreationTab(navigation, { newServiceCategoryId: serviceCategoryId });
      }
      if (route.params.goBackTo === 'new') {
        openNewWithGuard(navigation, {
          newServiceCategoryId: serviceCategoryId,
        });
      }
      if (route.params.goBackTo === 'one-listing') {
        navigateToListingWithParams(navigation, {
          id: route.params.listingId!,
          newServiceCategoryId: serviceCategoryId,
        });
      }
      if (route.params.goBackTo === 'edit-listing') {
        navigation.navigate(
          'edit-listing',
          {
            id: route.params.listingId!,
            newServiceCategoryId: serviceCategoryId,
          },
          { pop: true },
        );
      }
      if (route.params.goBackTo === 'set-atom-then-close') {
        setSelectedCategory(serviceCategoryId === route.params.selectedCategory ? [] : [serviceCategoryId]);
        navigation.goBack();
      }
      return;
    }

    const screenParams = {
      defaultValues: {
        type: ListingType.request,
        subtype: ListingSubtype.service,
        categoryPrimaryId: SERVICE_CATEGORY_ID,
        serviceCategoryId,
      },
    };
    openNewWithGuard(navigation, screenParams, 'replace');
  };

  return (
    <Page isModal>
      <Column gap="spacing.general.x8">
        <Row>
          {Platform.OS === 'android' && (
            <Pressable onPress={() => navigation.goBack()}>
              {route.params?.goBackTo ? (
                <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} />
              ) : (
                <IconX strokeWidth={ICON_STROKE_WIDTH} />
              )}
            </Pressable>
          )}
          <Text flex={1} fontWeight="600" textAlign="center" variant="headerXSmall">
            {t('prompt.serviceRequest.title')}
          </Text>
        </Row>

        <Column gap="spacing.general.x2">
          {serviceCategories?.map((cat) => (
            <RenderedButton
              isSelected={route.params?.selectedCategory === cat.id}
              key={cat.id}
              onPress={() => navigateToNew(cat.id)}
              text={t(`serviceCategories.${cat.name}.desc`, { ns: 'common' })}
              title={t(`serviceCategories.${cat.name}.name`, { ns: 'common' })}
            />
          ))}
        </Column>
        <Column
          borderRadius="radius.lg"
          gap="spacing.general.x4"
          style={{ backgroundColor: '#EDD441', padding: theme.spacing['spacing.general.x6'] }}
        >
          <Row alignItems="center" gap="spacing.general.x2">
            <IconHelpCircle strokeWidth={ICON_STROKE_WIDTH} />
            <Text flex={1} fontWeight="700" variant="bodyLarge">
              {t('prompt.serviceRequest.missing.title')}
            </Text>
          </Row>
          <Text variant="bodyBase">{t('prompt.serviceRequest.missing.text')}</Text>
          <Button
            onPress={() =>
              navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
                i18nKey: `prompt.serviceRequest.missing.chatPrompt`,
                ns: 'new',
                values: { name: myProfile?.firstName ?? '-' },
              })
            }
            style={{
              marginTop: theme.spacing['spacing.general.x2'],
              paddingHorizontal: theme.spacing['spacing.general.x4'],
              borderRadius: theme.borderRadii['radius.xl'],
              height: 2 * theme.borderRadii['radius.xl'],
            }}
            suffix={<ChatBubble color={theme.colors['surface.default']} />}
            variant="primary"
          >
            {t('prompt.serviceRequest.missing.ctaButton')}
          </Button>
        </Column>
      </Column>
    </Page>
  );
};

export const ServiceCategoriesScreen: NavigationScreen<'serviceCategories'> = withErrorBoundary(
  ServiceCategoriesScreenComponent,
);

const RenderedButton = ({
  title,
  text,
  onPress,
  isSelected,
}: {
  title: string;
  text: string;
  onPress: ButtonProps['onPress'];
  isSelected?: boolean;
}) => {
  return (
    <Button
      onPress={onPress}
      style={{
        borderRadius: 43,
        paddingHorizontal: theme.spacing['spacing.general.x8'],
        paddingVertical: theme.spacing['spacing.general.x4'],
        backgroundColor: isSelected
          ? theme.colors['surface.inverse.tertiary-neutral']
          : theme.colors['surface.default'],
      }}
      variant="secondaryLight"
    >
      <Column flex={1} gap="spacing.general.x1">
        <Text fontWeight="700" numberOfLines={1} variant="bodyBase">
          {title}
        </Text>
        <Text
          ellipsizeMode="tail"
          numberOfLines={2}
          style={{ color: theme.colors['icon.tertiary'] }}
          variant="bodyBase"
        >
          {text}
        </Text>
      </Column>
    </Button>
  );
};
